import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthStateType } from './useAuthState';
import { checkRateLimit, generateSecureId } from '@/utils/security';
import { toast } from 'sonner';

/**
 * Secure login hook with rate limiting and account lockout
 */
export const useSecureLogin = (authState: AuthStateType) => {
  const { setSession, setUser, failedAttempts, setFailedAttempts, isMounted } = authState;

  const secureLogin = async (email: string, password: string) => {
    // Check rate limiting
    if (!checkRateLimit('auth:login', 5, 60000)) {
      return {
        error: new Error('Too many login attempts. Please try again later.')
      };
    }

    // Check for account lockout
    const attemptKey = email.toLowerCase();
    const attemptRecord = failedAttempts[attemptKey];
    
    if (attemptRecord) {
      const lockoutDuration = 15 * 60 * 1000; // 15 minutes
      const timeSinceLastFailure = Date.now() - attemptRecord.lastFailure;
      
      if (attemptRecord.count >= 5 && timeSinceLastFailure < lockoutDuration) {
        const remainingTime = Math.ceil((lockoutDuration - timeSinceLastFailure) / 60000);
        return {
          error: new Error(`Account temporarily locked. Try again in ${remainingTime} minutes.`)
        };
      }
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Track failed attempt
        setFailedAttempts(prev => ({
          ...prev,
          [attemptKey]: {
            count: (prev[attemptKey]?.count || 0) + 1,
            lastFailure: Date.now()
          }
        }));
        
        return { error };
      }

      // Clear failed attempts on successful login
      setFailedAttempts(prev => {
        const newAttempts = { ...prev };
        delete newAttempts[attemptKey];
        return newAttempts;
      });

      if (isMounted.current) {
        setSession(data.session);
        setUser(data.user);
        
        // Generate secure session ID
        const sessionId = generateSecureId();
        sessionStorage.setItem('sessionId', sessionId);
      }

      return { data, error: null };
    } catch (error: any) {
      return { error };
    }
  };

  return { secureLogin };
};
